// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import xCoveoUpdate from "@eisinc/App/CoveoConnector/Handlers/Coveo";
import { trackSearchEvent } from "@eisinc/App/CoveoConnector/Services/CoveoService";
import { getCoveoConnectorMapState } from "@eisinc/App/CoveoConnector/Store/Selectors/CoveoSelectors";
import { SearchEventRequest } from "@eisinc/App/CoveoConnector/Types/schema";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    trackCoveoEvents = "trackCoveoEvents",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.trackCoveoEvents]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        ...getCoveoConnectorMapState(state),
    };
};

const mapDispatchToProps = {
    xCoveoUpdate,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface CoveoConnectorStyles {
    container?: InjectableCss;
}

const coveoConnectorStyles: CoveoConnectorStyles = {
    container: {
        css: css`
            background: #e5e5e5;
            padding: 10px;
        `,
    },
};

const styles = coveoConnectorStyles;
const CoveoConnector: FC<Props> = ({
    xCoveoUpdate,
    correctedQuery,
    originalQuery,
    analyticsObject,
    numberOfResults,
    fields,
    isConfiguredForCoveo,
    query,
    location,
    didYouMeanSuggestions,
    searchPath,
}) => {
    const { isInShell } = useShellContext();
    const dispatch = useDispatch();
    // Placeholder in CMS view
    if (isInShell) {
        return (
            <StyledWrapper {...styles.container} data-test-selectore="CoveoConnector">
                <Typography>{translate("Coveo Connector Widget")}</Typography>
            </StyledWrapper>
        );
    }

    useEffect(() => {
        if (!fields.trackCoveoEvents || !isConfiguredForCoveo) {
            return;
        }

        /**
         * On widget load
         * if fields.trackCoveoEvents is checked
         * and isConfiguredForCoveo are ready from backend
         * enable coveo tracker
         */
        xCoveoUpdate({
            trackCoveo: true,
        });
    }, [fields, isConfiguredForCoveo]);

    /**
     * Instead of overriding ProductListSuggestion widget to trigger the event
     * watch correctedQuery and originalQuery
     * OOTB updates those variables on search list page load
     * matching below condition means Opti corrected the query automatically
     * and we should trigger "didyoumeanAutomatic" event
     */
    useEffect(() => {
        if (correctedQuery && correctedQuery !== originalQuery) {
            const didyoumeanAutomaticEventParameter: SearchEventRequest = {
                actionCause: "didyoumeanAutomatic",
                numberOfResults,
                searchQueryUid: analyticsObject?.searchQueryUid || translate("my-uid"),
                originLevel1: "listing",
                searchHub: "listing",
                queryText: correctedQuery,
                responseTime: analyticsObject?.responseTime || 1,
            };
            trackSearchEvent(didyoumeanAutomaticEventParameter);
        }
    }, [correctedQuery]);

    useEffect(() => {
        // Track searchboxSubmit if a user searched for a term
        if (query && originalQuery !== query && location?.pathname.replace('/', '') === searchPath && typeof localStorage !== 'undefined') {
            const searchEventParameters: SearchEventRequest = {
                actionCause: "searchboxSubmit",
                numberOfResults: 0,
                queryText: query,
                responseTime: 0,
                searchQueryUid: translate("my-uid"),
                originLevel1: "listing",
                searchHub: "search",
                // Other optional parameters https://docs.coveo.com/en/1502/build-a-search-ui/log-search-events#optional-request-body-properties
                advancedQuery: "",
                queryPipeline: "",
            };
            
            localStorage?.removeItem('isSearchListActive');

            dispatch({
                type: "xCoveo/setSearchboxSubmitEvent",
                searchEventParameters,
            } as any);
        }

        /**
         * https://docs.coveo.com/en/1389/build-a-search-ui/standard-actions-and-usage-analytics-reference#correcting-queries
         * Track didyoumeanClick if a user selected one of the didYouMeanSuggestions on SLP
         * originalQuery will be the query he entered before correction
         * query will be the selected query from didYouMeanSuggestions
         * added location?.pathname === searchPath to make sure we are in the SLP
         */
        if(originalQuery !== query && didYouMeanSuggestions && location?.pathname.replace('/','') === searchPath) {
            const isCorrectedManual = didYouMeanSuggestions.findIndex(q => q.suggestion === query) > -1;
            if(isCorrectedManual) {
                const didyoumeanClickEventParameter: SearchEventRequest = {
                    actionCause: "didyoumeanClick",
                    numberOfResults,
                    searchQueryUid: analyticsObject?.searchQueryUid || translate("my-uid"),
                    originLevel1: "listing",
                    searchHub: "listing",
                    queryText: query!,
                    responseTime: analyticsObject?.responseTime || 1,
                };
                dispatch({
                    type: "xCoveo/setLastDidyoumeanClickEvent",
                    didyoumeanClickEvent: didyoumeanClickEventParameter,
                } as any);
            }
        }
    }, [query, originalQuery]);
    
    return null;
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CoveoConnector),
    definition: {
        displayName: "Coveo Connector",
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.trackCoveoEvents,
                displayName: "Track Coveo Events",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
